/**
 * Closing the trading day.
 *
 * The shop decides when the day ends — not the clock. Everything rung up until
 * now stays on the day being closed; from here on it belongs to the next one.
 * The next day defaults to the day after this one but can be changed, for a
 * shop that skipped a day or wants to name the date itself.
 *
 * The day's takings are shown before closing, so it is a decision made looking
 * at the numbers rather than in the dark — and shown again once closed, the way
 * a shift's count is, so there is a moment to read them before handing on.
 */
import { useState } from 'react';
import { formatPKR } from '@foodrun/shared';

import { Button } from '../../components/ui/Button.jsx';
import { useAuth } from '../../stores/auth.js';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * A YYYY-MM-DD as a person reads it: "12 Sep 2026".
 *
 * Built by hand rather than through toLocaleDateString so it reads the same on
 * every device — the locale formatter renders "en-PK" differently across
 * browsers and leaves the date looking unlike itself from one till to the next.
 */
function niceDate(iso) {
  const [y, m, d] = String(iso ?? '').split('-').map(Number);
  if (!y || !m || !d) return iso || '';
  return `${String(d).padStart(2, '0')} ${MONTHS[m - 1]} ${y}`;
}

export function DayClosePanel({ current, onClose, onCancel, busy, error, result }) {
  const open = current?.open ?? null;
  const summary = current?.summary ?? null;
  const [nextDate, setNextDate] = useState(current?.nextDate ?? '');
  const [notes, setNotes] = useState('');
  // Only an owner may open a day that is not the next one — for putting right a day opened wrong.
  const isOwner = useAuth((s) => s.hasRole)('owner', 'super_admin');
  const refusedDate = error?.code === 'NOT_NEXT_DAY';

  // Once closed, show what it closed on and where the shop is now.
  if (result) return <DayClosed result={result} onDone={onCancel} />;

  return (
    <div className="mx-auto max-w-lg p-4">
      <h1 className="font-semibold text-xl text-ink">Close the day</h1>
      <p className="mt-1.5 text-sm text-ink-2">
        {open ? (
          <>
            Closing <span className="font-medium text-ink">{niceDate(open.businessDate)}</span>.
            Everything rung up until now stays on this day; the next day starts fresh.
          </>
        ) : (
          'No day is open yet — closing will simply start the day you choose.'
        )}
      </p>

      {summary && (
        <dl className="mt-4 grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-rule bg-rule sm:grid-cols-4">
          <Stat label="Orders" value={summary.orders} />
          <Stat label="Sales" value={formatPKR(summary.grossSales)} />
          <Stat label="Refunds" value={formatPKR(summary.refunds)} />
          <Stat label="Voids" value={summary.voids} />
        </dl>
      )}

      <label className="mt-5 block">
        <span className="mb-1.5 block text-sm font-medium text-ink-2">The next day is</span>
        <input
          type="date"
          value={nextDate}
          onChange={(e) => setNextDate(e.target.value)}
          className="h-11 w-full rounded-lg border border-rule bg-surface-3 px-3 text-ink tabular-nums focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
        />
        <span className="mt-1 block text-xs text-ink-3">
          Always the day after: every sale is filed under the day that is open, so a day opened on the wrong date would mix two nights in every report. Only an owner can open another date.
        </span>
      </label>

      <label className="mt-4 block">
        <span className="mb-1.5 block text-sm font-medium text-ink-2">Notes (optional)</span>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          placeholder="Anything worth recording about the day"
          className="w-full rounded-lg border border-rule bg-surface-3 px-3.5 py-2.5 text-[15px] text-ink placeholder:text-ink-3 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
        />
      </label>

      {error && (
        <p role="alert" className="mt-4 rounded-lg bg-bad-bg px-3.5 py-2.5 text-sm text-bad">
          {error.message}
        </p>
      )}
      {refusedDate && isOwner && (
        <button
          type="button"
          disabled={busy}
          onClick={() => onClose({ nextDate, force: true, ...(notes.trim() ? { notes: notes.trim() } : {}) })}
          className="mt-2 w-full rounded-lg border border-rule px-3 py-2 text-sm font-medium text-ink-2 hover:bg-surface-3 disabled:opacity-40"
        >
          Open {nextDate} anyway (owner)
        </button>
      )}

      <div className="mt-5 flex gap-2">
        <Button variant="secondary" size="lg" onClick={onCancel} disabled={busy}>
          Back
        </Button>
        <Button
          size="lg"
          className="flex-1"
          busy={busy}
          disabled={!nextDate}
          onClick={() =>
            onClose({ nextDate, ...(notes.trim() ? { notes: notes.trim() } : {}) })
          }
        >
          Close the day
        </Button>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-surface p-3 text-center">
      <dt className="text-xs text-ink-3">{label}</dt>
      <dd className="mt-0.5 font-semibold tabular-nums text-ink">{value}</dd>
    </div>
  );
}

/** What the day closed on, and where the shop is now. */
function DayClosed({ result, onDone }) {
  const closed = result.closed;
  const s = closed?.summary;

  return (
    <div className="mx-auto max-w-md p-4 text-center">
      <h1 className="font-semibold text-xl text-ink">Day closed</h1>
      {closed && (
        <p className="mt-1.5 text-sm text-ink-2">{`${niceDate(closed.businessDate)} is done.`}</p>
      )}

      {s && (
        <dl className="mt-5 space-y-2 rounded-xl border border-rule bg-surface p-4 text-left">
          <Row label="Orders" value={s.orders} />
          <Row label="Sales" value={formatPKR(s.grossSales)} />
          <Row label="Discounts" value={formatPKR(s.discounts)} />
          <Row label="Tax" value={formatPKR(s.tax)} />
          <Row label="Refunds" value={formatPKR(s.refunds)} />
          {s.voids > 0 && <Row label="Voids" value={s.voids} />}
        </dl>
      )}

      <p className="mt-4 rounded-lg bg-good-bg px-3 py-2.5 text-sm text-good">
        {`Now trading on ${niceDate(result.opened?.businessDate)}.`}
      </p>

      <Button size="lg" className="mt-6 w-full" onClick={onDone}>
        Done
      </Button>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline justify-between">
      <dt className="text-sm text-ink-2">{label}</dt>
      <dd className="tabular-nums text-ink">{value}</dd>
    </div>
  );
}
